import { Injectable } from '@angular/core';
import { PxdAgGridActionRenderer } from '@app/shared/aggrid/pxd-ag-grid-action-renderer';
import { PxdAgGridNoteEditorComponent } from '@app/shared/aggrid/pxd-ag-grid-note-editor';
import { PxdAgEditGridDefaultColumnDef, PxdAgGridColumnTypes, PxdAgGridOptions, PxdAgGridSideBarOptions, PxdAgGridStatusBarOptions } from "@app/shared/aggrid/pxd-ag-grid-options";
import { PxdAgGridSelectEditorComponent } from '@app/shared/aggrid/pxd-ag-grid-select-editor';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { IItemDto } from '@app/shared/models/dtos/item';
import { generateSvgStringWithOverlay } from '@app/shared/utilities/icon-utilities';
import { ColDef, ICellRendererParams, MenuItemDef, ValueFormatterParams, ValueParserParams } from 'ag-grid-community';
import { BehaviorSubject, Subject } from 'rxjs';
import { first } from 'rxjs/operators';

export class ProjectScopingItemsGridOptions implements PxdAgGridOptions {
  enableCharts = true;
  cellSelection = true;
  rowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never' = 'never';
  defaultColDef = PxdAgEditGridDefaultColumnDef;
  statusBar = PxdAgGridStatusBarOptions;
  sideBar = PxdAgGridSideBarOptions;
  columnTypes = PxdAgGridColumnTypes;
  headerHeight = 50;
  autoGroupColumnDef = {
    width: 150,
    headerName: 'Item Scoping Category',
    suppressSizeToFit: true,
    cellRendererParams: {
      suppressCount: true
    }
  }
  columnDefs = [];
  getContextMenuItems = null;
}

@Injectable()
export class ProjectScopingItemsGridOptionService {
  public projectScopingItemsGridOptions: ProjectScopingItemsGridOptions = new ProjectScopingItemsGridOptions();
  private icons = kendoIcons;
  public RemoveItemEvent: BehaviorSubject<ICellRendererParams>;

  NotePopup: boolean = false;
  NoteEvent: Subject<any> = new Subject<any>();

  public constructor() {
    this.projectScopingItemsGridOptions.columnDefs = this.columnDefs;
    this.projectScopingItemsGridOptions.getContextMenuItems = (params) => this.contextMenuItems(params);

    this.RemoveItemEvent = new BehaviorSubject(null);
  }

  ItemsMap: Map<number, IItemDto> = new Map();

  private getItemName = (id) => {
    if (!id) return '(Select Item)';

    let item = this.ItemsMap.get(id);

    if (item) {
      return item.Name;
    }

    return '';
  }

  private removeItemAction = (cell: ICellRendererParams) => {
    this.RemoveItemEvent.next(cell);
  }

  contextMenuItems(params): (string | MenuItemDef)[] {
    let result: (string | MenuItemDef)[] = [
      'copy',
      'copyWithHeaders',
      'paste',
      'export',
    ];
    //TODO only show note edit ability for items that have already been saved
    if (!params.node.group && params.column.colId == 'ItemId' && params.node.data.ProjectTankBatteryScopingItemId > 0) {
      let noteOptions: MenuItemDef[] = [
        {
          name: 'New Note',
          disabled: this.newNoteDisabled(params),
          action: () => { this.addEditNote(params) },
          icon: generateSvgStringWithOverlay(this.icons.note, this.icons.plus),
        },
        {
          name: 'Edit Note',
          disabled: this.editNoteDisabled(params),
          action: () => { this.addEditNote(params) },
          icon: generateSvgStringWithOverlay(this.icons.note, this.icons.edit),
        },
        {
          name: 'Delete Note',
          disabled: this.deleteNoteDisabled(params),
          action: () => { this.deleteNote(params) },
          icon: generateSvgStringWithOverlay(this.icons.note, this.icons.x),
        }];
      return result.concat(noteOptions);
    }
    return result;
  }

  addEditNote(params) {
    this.NotePopup = true;
    params.api.startEditingCell({
      rowIndex: params.node.rowIndex,
      colKey: params.column
    });
    this.NotePopup = false;
    this.NoteEvent.pipe(
      first()).subscribe(a => {
        params.api.stopEditing();
      });
  }

  deleteNote(params) {
    this.NoteEvent.next({
      action: 'save',
      params: params,
      note: ''
    });
    //params.api.refreshCells({force:true, columns:[params.column]});
  }

  newNoteDisabled(params): boolean {
    if (!params.node.data.ItemId || params.node.data.Note) { //TODO add in extra check to see if a note already exists
      return true;
    }
    return false;
  }

  editNoteDisabled(params): boolean {
    if (!params.node.data.ItemId || !params.node.data.Note) { //TODO add in extra check to see if a note already exists
      return true;
    }
    return false;
  }

  deleteNoteDisabled(params): boolean {
    if (!params.node.data.ItemId || !params.node.data.Note) { //TODO add in extra check to see if a note already exists
      return true;
    }
    return false;
  }

  columnDefs: ColDef[] = [
    {
      headerName: '',
      maxWidth: 30,
      editable: false,
      cellRenderer: PxdAgGridActionRenderer,
      valueGetter: (params) => ({
        hidden: params.node.group || params.data == null || params.data.ItemId == null
      }),
      cellRendererParams: {
        icon: this.icons.x,
        action: this.removeItemAction
      }
    },
    { field: 'ProjectTankeBatteryScopingItemId', hide: true, enableRowGroup: false },
    { field: 'ProjectId', hide: true, enableRowGroup: false },
    { field: 'ItemScopingCategoryId', hide: true, enableRowGroup: false },
    { field: 'ItemScopingCategoryDto.Description', rowGroup: true, hide: true },
    {
      field: 'ItemId',
      headerName: 'Item',
      enableRowGroup: false,
      valueFormatter: params => {
        if (params.node.group) {
          return '';
        }

        return this.getItemName(params.value);
      },
      cellRenderer: params => {
        if (params.node.group) { return params.valueFormatted; }
        if (params.data.Note && params.data.Note != '') { return `<div>${params.valueFormatted}</div><div class="triangle"></div>` }
        return params.valueFormatted;
      },
      tooltipField: 'Note',
      cellEditorSelector: params => {
        if (this.NotePopup) {
          return {
            component: PxdAgGridNoteEditorComponent,
            params: {
              MaxLength: 250,
              Note: params.data.Note,
              UnderlyingValue: params.value,
              Width: params.column.getActualWidth(),
              NoteEvent: this.NoteEvent
            },
            cellEditorPopup: true
          }
        }
        let rows: Array<any> = [];
        params.api.forEachNode(function (node) {
          rows.push(node.data);
        });

        let existingItemMap = new Map<number, boolean>();

        const thisRowItemId = params.node.data.ItemId;

        rows.filter(f => f != null && f.ItemId != null)
          .forEach(m => {
            if (m.ItemId != thisRowItemId) existingItemMap.set(m.ItemId, true)
          });

        //if this is a NEW row (has no scopingItemId value) then do not allow inactive items
        const isSavedRow = params.node.data.ProjectTankBatteryScopingItemId > 0;

        // make popup as big as possible (with buffer) without going off the edge of the page
        let bounding = params.eGridCell.getBoundingClientRect();
        let positionFromTop = Math.floor(bounding.top);
        let positionFromBottom = window.innerHeight - positionFromTop;
        let selectorPopupHeight = positionFromTop > positionFromBottom ? positionFromTop - 100 : positionFromBottom - 100;
        let selectorPopupWidth = bounding.width;
        let items = Array.from(this.ItemsMap.values())
          .filter(f => f.ItemScopingCategoryId === params.data.ItemScopingCategoryId &&
            !existingItemMap.has(f.ItemId) &&
            (isSavedRow || f.IsActive))
          .map(d => {
            return { value: d.ItemId, text: d.Name };
          });
        //the selector is a value primitive
        return {
          component: PxdAgGridSelectEditorComponent,
          params: {
            values: Array.from(this.ItemsMap.values())
              .filter(f => f.ItemScopingCategoryId === params.data.ItemScopingCategoryId &&
                !existingItemMap.has(f.ItemId) &&
                (isSavedRow || f.IsActive))
              .map(d => {
                return { value: d.ItemId, text: d.Name };
              }),
            popupHeight: selectorPopupHeight,
            value: params.data.ItemId,          //dropdown value is a primitive so this is correct
            clearButton: false,
            popupWidth: selectorPopupWidth
          },
          cellEditorPopup: true
        };
      }
    },
    { field: 'OilCapacity', editable: false, hide: true, enableRowGroup: false, valueFormatter: this.formatNumber, suppressHeaderMenuButton: true, maxWidth: 75, flex: 1 },
    { field: 'GasCapacity', editable: false, hide: true, enableRowGroup: false, valueFormatter: this.formatNumber, suppressHeaderMenuButton: true, maxWidth: 75, flex: 1 },
    { field: 'WaterCapacity', editable: false, hide: true, enableRowGroup: false, valueFormatter: this.formatNumber, suppressHeaderMenuButton: true, maxWidth: 75, flex: 1 },
    { field: 'ExistingQuantity', headerName: 'Existing Qty', editable: false, enableRowGroup: false, suppressHeaderMenuButton: true, maxWidth: 90, flex: 1, headerClass: 'center-aligned', cellStyle: { 'text-align': 'center' } },
    { field: 'Quantity', headerName: 'New Qty', editable: this.isEditable, enableRowGroup: false, suppressHeaderMenuButton: true, valueParser: this.forceNumber, maxWidth: 75, type: 'numericColumn', flex: 1, headerClass: 'center-aligned', cellStyle: { 'text-align': 'center' } },
    { field: 'TotalQuantity', headerName: 'Total Qty', editable: false, enableRowGroup: false, flex: 1, valueFormatter: this.totalQuantityFormatter, suppressHeaderMenuButton: true, maxWidth: 75, headerClass: 'center-aligned', cellStyle: { 'text-align': 'center' } },
    { field: 'BackupQuantity', headerName: 'Backup Qty', editable: true, enableRowGroup: false, suppressHeaderMenuButton: true, maxWidth: 90, type: 'numericColumn', flex: 1, headerClass: 'center-aligned', cellStyle: { 'text-align': 'center' } },
  ];

  private isEditable(params: ValueFormatterParams) {
    if (!params.data) {
      return false;
    }

    return params.data.ItemId != null;
  }

  private totalQuantityFormatter(params: ValueFormatterParams): string {
    if (!params.data || params.node.group || params.data.ItemId == null) {
      return null;
    }
    let result = +(params.data.ExistingQuantity || 0) + +(params.data.Quantity || 0);
    return result.toString();
  }

  private formatNumber(params: ValueFormatterParams) {
    let number = params.value;

    if (!number) return null;

    return Math.floor(number)
      .toString()
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  }
  private forceNumber(params: ValueParserParams): number {
    let number = params.newValue;

    if (!number) return 0;
    return Number(number);
  }
}